import React from "react";
import { FormattedMessage } from "react-intl";
import {
	Card,
	CircularProgress,
	Grid,
	IconButton,
	styled,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
} from "@mui/material";
import Refresh from "@mui/icons-material/Refresh";

import type { CartonPropertyGroup } from "@/schemas/carton-property-group";

import { CartonPropertyGroupEditButton } from "../components/carton-property-group-edit-button";
import { CartonPropertyGroupDeleteButton } from "../components/carton-property-group-delete-button";
import { CartonPropertyGroupHistoryButton } from "../components/carton-property-group-history-button";

export type AssignedProductionGroups = Record<string, string>;

const StyledTableRow = styled(TableRow)(({ theme }) => ({
	"&:nth-of-type(even)": {
		backgroundColor: theme.palette.action.hover,
	},
}));

export interface CartonPropertyGroupsTemplateProps {
	readonly cartonPropertyGroups: CartonPropertyGroup[];
	readonly assignedProductionGroups: AssignedProductionGroups;
	readonly isLoading: boolean;
	readonly isError: boolean;
	readonly onRetry: () => void;
}

export const CartonPropertyGroupsTemplate: React.FC<CartonPropertyGroupsTemplateProps> = ({
	cartonPropertyGroups,
	assignedProductionGroups,
	isLoading,
	isError,
	onRetry,
}) => {
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(50);

	return (
		<Card component={Grid} container direction="column">
			<TableContainer sx={{ flex: 1 }}>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell>
								<FormattedMessage id="Name" defaultMessage="Name" />
							</TableCell>
							<TableCell>
								<FormattedMessage id="Production Groups" defaultMessage="Production Groups" />
							</TableCell>
							<TableCell>
								<FormattedMessage id="Ratio" defaultMessage="Ratio" />
							</TableCell>
							<TableCell />
						</TableRow>
					</TableHead>

					<TableBody>
						{isLoading === true && (
							<StyledTableRow sx={{ height: 100 }}>
								<TableCell colSpan={1000} align="center" sx={{ borderBottom: "none" }}>
									<CircularProgress color="info" data-testid="carton-property-groups__progress__loading" />
								</TableCell>
							</StyledTableRow>
						)}
						{isError === true && isLoading === false && (
							<StyledTableRow sx={{ height: 100 }}>
								<TableCell colSpan={1000} align="center" sx={{ borderBottom: "none" }}>
									<Grid container justifyContent="center" alignItems="center" gap={2}>
										<Typography component="span" color="error" data-testid="carton-property-groups__text__error">
											<FormattedMessage
												id="Failed to load carton property groups. Please try again."
												defaultMessage="Failed to load carton property groups. Please try again."
											/>
										</Typography>
										<IconButton onClick={onRetry} data-testid="carton-property-groups__button__retry">
											<Refresh />
										</IconButton>
									</Grid>
								</TableCell>
							</StyledTableRow>
						)}
						{!isLoading && !isError && cartonPropertyGroups.length === 0 && (
							<StyledTableRow>
								<TableCell colSpan={1000} align="center">
									<Typography
										color="textDisabled"
										data-testid="carton-property-groups__text__no-carton-property-groups"
									>
										<FormattedMessage
											id="No Carton Property Groups Configured"
											defaultMessage="No Carton Property Groups Configured"
										/>
									</Typography>
								</TableCell>
							</StyledTableRow>
						)}

						{!isLoading &&
							!isError &&
							cartonPropertyGroups
								.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
								.map((cartonPropertyGroup) => (
									<StyledTableRow key={cartonPropertyGroup.id}>
										<TableCell sx={{ fontWeight: 500 }}>{cartonPropertyGroup.alias}</TableCell>
										<TableCell>{assignedProductionGroups[cartonPropertyGroup.id]}</TableCell>
										<TableCell>{cartonPropertyGroup.ratio}</TableCell>
										<TableCell>
											<Grid container gap={1} justifyContent="flex-end">
												<CartonPropertyGroupHistoryButton cartonPropertyGroup={cartonPropertyGroup} />
												<CartonPropertyGroupEditButton cartonPropertyGroup={cartonPropertyGroup} />
												<CartonPropertyGroupDeleteButton cartonPropertyGroup={cartonPropertyGroup} />
											</Grid>
										</TableCell>
									</StyledTableRow>
								))}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				rowsPerPageOptions={[50, 100, 500]}
				component="div"
				count={cartonPropertyGroups.length}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={(_, pageNumber) => setPage(pageNumber)}
				onRowsPerPageChange={(event) => {
					setRowsPerPage(parseInt(event.target.value));
					setPage(0);
				}}
			/>
		</Card>
	);
};
